import React, { useState, useEffect, useMemo } from 'react';
import ArtworkDataService from '../../services/ArtworkService';
import MapMultiPoint from '../MapMultiPoint/MapMultiPoint';
import MapDetailCard from './MapDetailCard';
import TimeStrip from '../TimeStrip/TimeStrip';
import ErrorLoading from '../Error/Error';
import Loading from '../Loading/Loading';

import FreeSearchFilter from '../Filters/FreeSearchFilter';
import CategoriesFilter from '../Filters/CategoriesFilter';
import SubjectFilter from '../Filters/SubjectFilter';
import LuogoConservazioneFilter from '../Filters/LuogoConservazioneFilter';

import './mapArtwork.scss';

import { useLanguage } from '../../LanguageContext';

/**
 * Mappa immersiva spazio-tempo: la mappa riempie la pagina, filtri,
 * barra del tempo e scheda dell'opera galleggiano sopra come card.
 */
const MapArtwork = () => {
  const [artworks, setArtworks] = useState([]);
  const [currentArtwork, setCurrentArtwork] = useState(null);
  const [searchName] = useState('');
  const pageSize = 100;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [activeArtworksList, setActiveArtworksList] = useState([]);
  const [searchData_inizio] = useState('');
  const [searchData_fine] = useState('');
  const { selectedLanguage } = useLanguage();
  const it = selectedLanguage === 'it';

  const [selectedTag, setSelectedTag] = useState('');
  const [tags, setTags] = useState([]);
  const [subjectTags, setSubjectTags] = useState([]);
  const [selectedSubjectTag, setSelectedSubjectTag] = useState('');
  const [search_luogo_conservazione, setSearch_luogo_conservazione] = useState('');
  const [freeSearch, setFreeSearch] = useState('');
  // su mobile la card filtri parte ripiegata: prima la mappa
  const [filtersOpen, setFiltersOpen] = useState(window.innerWidth >= 768);

  const [hoveredArtworkId, setHoveredArtworkId] = useState(null);

  // finestra temporale della mappa spazio-tempo (secoli IV-XV)
  const [timeWindow, setTimeWindow] = useState({ from: 4, to: 15 });
  const fullTime = timeWindow.from === 4 && timeWindow.to === 15;
  const activeIds = useMemo(() => {
    const start = (timeWindow.from - 1) * 100 + 1;
    const end = timeWindow.to * 100;
    const ids = new Set();
    artworks.forEach((a) => {
      const dated = (a.dates || []).filter(d => d.data_inizio && d.data_fine);
      if (dated.length === 0) {
        if (fullTime) ids.add(a.id);
        return;
      }
      if (dated.some(d => d.data_inizio <= end && d.data_fine >= start)) {
        ids.add(a.id);
      }
    });
    return ids;
  }, [artworks, timeWindow, fullTime]);

  const onChangeSelectedTag = (e) => setSelectedTag(e.target.value);
  const onChangeSelectedSubjectTag = (e) => setSelectedSubjectTag(e.target.value);
  const onChangeLuogoConservazione = (e) => setSearch_luogo_conservazione(e.target.value);
  const onChangeFreeSearch = (e) => setFreeSearch(e.target.value);

  const retrieveTags = () => {
    ArtworkDataService.getTags()
      .then((response) => setTags(response.response || []))
      .catch((e) => console.error(e));
  };

  const retrieveSubjectTags = () => {
    ArtworkDataService.getSubjectTags()
      .then((response) => setSubjectTags(response.response || []))
      .catch((e) => console.error(e));
  };

  const retrieveArtworks = () => {
    setLoading(true);
    setError(false);
    setCurrentArtwork(null);
    ArtworkDataService.getAll(1, pageSize, searchName, searchData_inizio, searchData_fine, selectedTag, selectedSubjectTag, search_luogo_conservazione, selectedLanguage, freeSearch)
      .then((response) => {
        setArtworks(response.artworks);
      })
      .catch((e) => {
        console.error(e);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    retrieveTags();
    retrieveSubjectTags();
    retrieveArtworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const setActiveArtwork = (artwork) => {
    // dati leggeri subito, scheda completa (con immagini) in arrivo
    setCurrentArtwork(artwork);
    if (artwork && artwork.id) {
      ArtworkDataService.get(artwork.id)
        .then((response) => {
          if (response && response.response) {
            setCurrentArtwork(response.response);
          }
        })
        .catch((e) => console.error(e));
    }
  };

  return (
    <div className="map-stage">
      <div className="map-canvas">
        <MapMultiPoint
          points={artworks}
          setActiveArtworksList={setActiveArtworksList}
          setActiveArtwork={setActiveArtwork}
          selectedArtworkId={currentArtwork ? currentArtwork.id : null}
          hoveredArtworkId={hoveredArtworkId}
          activeIds={activeIds}
          onShowAllTime={() => setTimeWindow({ from: 4, to: 15 })}
        />
      </div>

      {/* card filtri (contorno, alto a sinistra) */}
      <div className="map-card map-card-filters">
        <div
          className="map-card-title"
          onClick={() => setFiltersOpen((o) => !o)}
        >
          <span>🔎 {it ? 'Cerca nel catalogo' : 'Search the catalogue'}</span>
          <span>{filtersOpen ? '▾' : '▸'}</span>
        </div>
        {filtersOpen && (
          <>
            <FreeSearchFilter
              freeSearch={freeSearch}
              onChangeFreeSearch={onChangeFreeSearch}
              retrieveArtworks={retrieveArtworks}
            />
            <CategoriesFilter
              tags={tags}
              selectedTag={selectedTag}
              retrieveArtworks={retrieveArtworks}
              onChangeSelectedTag={onChangeSelectedTag}
            />
            <SubjectFilter
              subjectTags={subjectTags}
              selectedSubjectTag={selectedSubjectTag}
              retrieveArtworks={retrieveArtworks}
              onChangeSelectedSubjectTag={onChangeSelectedSubjectTag}
            />
            <LuogoConservazioneFilter
              search_luogo_conservazione={search_luogo_conservazione}
              onChangeLuogoConservazione={onChangeLuogoConservazione}
              retrieveArtworks={retrieveArtworks}
            />
            <small className="text-muted">
              {it
                ? 'Il periodo si sceglie sulla barra del tempo in basso.'
                : 'Pick the period on the time bar below.'}
            </small>
          </>
        )}
      </div>

      {/* card-opera interattiva (alto a destra) */}
      <MapDetailCard
        artwork={currentArtwork}
        activeList={activeArtworksList}
        onClose={() => setCurrentArtwork(null)}
        onSelect={setActiveArtwork}
        onHover={setHoveredArtworkId}
      />

      {/* barra del tempo flottante (basso, centrata) */}
      <div className="map-card-time">
        <TimeStrip
          window={timeWindow}
          onChange={setTimeWindow}
          activeCount={activeIds.size}
        />
      </div>

      {/* overlay caricamento / errore */}
      {loading && (
        <div className="map-overlay-center">
          <Loading />
        </div>
      )}
      {error && (
        <div className="map-overlay-center">
          <ErrorLoading />
          <button
            className="btn btn-outline-secondary mt-2"
            onClick={retrieveArtworks}
          >
            {it ? 'Riprova' : 'Retry'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MapArtwork;
